import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useThemeClasses } from "@/hooks/useThemeClasses";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, Search, Edit, Trash2, Filter } from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const ITEMS_PER_PAGE = 15;

const Produtos = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const lojaSlug = slug || user?.loja_slug;
  const { isDark, card, input, textPrimary, textSecondary, textMuted, tableRow, button } = useThemeClasses();
  const [produtos, setProdutos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modeloFilter, setModeloFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Recarregar dados sempre que a página for acessada
  useEffect(() => { 
    if (lojaSlug) {
      setLoading(true);
      fetchData(); 
    }
  }, [lojaSlug, location.key]);
  
  useEffect(() => { setCurrentPage(1); }, [search, modeloFilter]);

  const fetchData = async () => {
    try {
      const [produtosRes, modelosRes] = await Promise.all([
        axios.get(`${API}/loja/${lojaSlug}/produtos`, { params: { vendido: false } }),
        axios.get(`${API}/loja/${lojaSlug}/modelos`)
      ]);
      setProdutos(produtosRes.data);
      setModelos(modelosRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/loja/${lojaSlug}/produtos/${deleteId}`);
      toast.success("Celular excluído");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    } finally {
      setDeleteId(null);
    }
  };

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const filteredProdutos = produtos.filter((produto) => {
    const matchesSearch = 
      produto.cor?.toLowerCase().includes(search.toLowerCase()) || 
      produto.armazenamento?.toLowerCase().includes(search.toLowerCase()) || 
      produto.memoria_ram?.toLowerCase().includes(search.toLowerCase()) || 
      produto.modelo_nome?.toLowerCase().includes(search.toLowerCase()) ||
      produto.imei?.includes(search);
    const matchesModelo = modeloFilter === "all" || produto.modelo_id === modeloFilter;
    return matchesSearch && matchesModelo;
  });

  // Pagination
  const totalPages = Math.ceil(filteredProdutos.length / ITEMS_PER_PAGE);
  const paginatedProdutos = filteredProdutos.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-[#D4AF37]">Carregando...</div></div>;

  return (
    <div className="space-y-6" data-testid="produtos-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Package className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold font-['Outfit'] ${textPrimary}`}>Celulares</h1>
            <p className={`text-sm ${textMuted}`}>{filteredProdutos.length} celulares em estoque</p>
          </div>
        </div>
        <Link to={`/${lojaSlug}/produtos/novo`}>
          <Button className="bg-[#D4AF37] text-black font-bold hover:bg-[#B5952F]" data-testid="btn-novo-produto">
            <Plus className="w-4 h-4 mr-2" />Novo Celular
          </Button>
        </Link>
      </div>

      <Card className={card}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input 
                placeholder="Buscar por modelo, cor, memória ou IMEI..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
                className={`pl-10 ${input}`}
                data-testid="search-produto" 
              />
            </div>
            <Select value={modeloFilter} onValueChange={setModeloFilter}>
              <SelectTrigger className={`w-full md:w-[200px] ${input}`} data-testid="filter-modelo">
                <Filter className="w-4 h-4 mr-2 text-gray-500" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent className={isDark ? 'bg-[#141414] border-white/10' : 'bg-white border-gray-200'}>
                <SelectItem value="all" className={textSecondary}>Todos os modelos</SelectItem>
                {modelos.map((m) => (
                  <SelectItem key={m.id} value={m.id} className={textSecondary}>{m.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className={card}>
        <CardContent className="p-0">
          {paginatedProdutos.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className={`${isDark ? 'border-white/5' : 'border-gray-100'} hover:bg-transparent`}>
                    <TableHead className={`uppercase text-xs ${textMuted}`}>Modelo</TableHead>
                    <TableHead className={`uppercase text-xs ${textMuted}`}>Cor</TableHead>
                    <TableHead className={`uppercase text-xs ${textMuted}`}>Armazenamento</TableHead>
                    <TableHead className={`uppercase text-xs ${textMuted}`}>Memória RAM</TableHead>
                    <TableHead className={`uppercase text-xs ${textMuted}`}>Bateria</TableHead>
                    <TableHead className={`uppercase text-xs ${textMuted}`}>Preço</TableHead>
                    <TableHead className={`uppercase text-xs ${textMuted}`}>Valor Compra</TableHead>
                    <TableHead className={`uppercase text-xs ${textMuted}`}>Lucro Est.</TableHead>
                    <TableHead className={`uppercase text-xs text-right ${textMuted}`}>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedProdutos.map((produto) => (
                    <TableRow key={produto.id} className={tableRow}>
                      <TableCell className="text-[#D4AF37] font-medium">{produto.modelo_nome}</TableCell>
                      <TableCell className={textSecondary}>{produto.cor}</TableCell>
                      <TableCell className={textSecondary}>{produto.armazenamento || produto.memoria}</TableCell>
                      <TableCell className={textSecondary}>{produto.memoria_ram || "-"}</TableCell>
                      <TableCell className={textSecondary}>{produto.bateria ? `${produto.bateria}%` : "-"}</TableCell>
                      <TableCell className="text-green-500 font-semibold">{formatCurrency(produto.preco)}</TableCell>
                      <TableCell className={`font-semibold ${textSecondary}`}>{formatCurrency(produto.valor_compra)}</TableCell>
                      <TableCell className="text-[#D4AF37] font-semibold">{formatCurrency((produto.preco || 0) - (produto.valor_compra || 0))}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/${lojaSlug}/produtos/editar/${produto.id}`}>
                            <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-400 hover:bg-blue-400/10">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-400 hover:bg-red-400/10" onClick={() => setDeleteId(produto.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredProdutos.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPage}
              />
            </>
          ) : (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className={textMuted}>Nenhum celular encontrado</p>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className={isDark ? 'bg-[#141414] border-white/10' : 'bg-white border-gray-200'}>
          <AlertDialogHeader>
            <AlertDialogTitle className={textPrimary}>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className={textMuted}>Tem certeza que deseja excluir este celular?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDark ? 'bg-white/5 border-white/10 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-700'}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Produtos;
